import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  Users, 
  Search,
  Loader2,
  Mail,
  Phone,
  Building,
  MapPin,
  Package,
  Eye,
  UserCheck,
  Clock,
  RefreshCw,
  FileText,
  Calendar,
  DollarSign,
  TrendingUp,
  ChevronRight,
  X,
  User
} from 'lucide-react';
import { formatDate, formatCurrency } from '../../lib/utils';
import { toast } from 'sonner';

function AgentCustomers() {
  const [customers, setCustomers] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerBookings, setCustomerBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  useEffect(function() {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [customersRes, pendingRes] = await Promise.all([
        api.get('/agent/customers'),
        api.get('/users/pending')
      ]);
      setCustomers(customersRes.data);
      setPendingCount(pendingRes.data.length);
    } catch (err) {
      console.error('Failed to load customers:', err);
      try {
        const bookingsRes = await api.get('/bookings');
        const customerMap = {};
        bookingsRes.data.forEach(function(booking) {
          if (booking.user_id && !customerMap[booking.user_id]) {
            customerMap[booking.user_id] = {
              id: booking.user_id,
              booking_count: 1,
              total_value: booking.price || 0
            };
          } else if (booking.user_id) {
            customerMap[booking.user_id].booking_count++;
            customerMap[booking.user_id].total_value += booking.price || 0;
          }
        });
        setCustomers(Object.values(customerMap));
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadCustomerBookings(customerId) {
    setLoadingBookings(true);
    try {
      const response = await api.get('/bookings');
      const userBookings = response.data.filter(function(b) { 
        return b.user_id === customerId; 
      });
      setCustomerBookings(userBookings);
    } catch (err) {
      console.error('Failed to load customer bookings:', err);
      setCustomerBookings([]);
    } finally {
      setLoadingBookings(false);
    }
  }

  function handleViewCustomer(customer) {
    setSelectedCustomer(customer);
    loadCustomerBookings(customer.id);
  }

  function closeModal() {
    setSelectedCustomer(null);
    setCustomerBookings([]);
  }

  const filteredCustomers = customers.filter(function(customer) {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (customer.email || '').toLowerCase().includes(term) ||
           (customer.first_name || '').toLowerCase().includes(term) ||
           (customer.last_name || '').toLowerCase().includes(term) ||
           (customer.company || '').toLowerCase().includes(term);
  }).filter(function(customer) {
    if (statusFilter === 'all') return true;
    return customer.status === statusFilter;
  });

  const statusCounts = {
    all: customers.length,
    approved: customers.filter(function(c) { return c.status === 'approved'; }).length,
    pending_approval: customers.filter(function(c) { return c.status === 'pending_approval'; }).length
  };

  const totalBookings = customers.reduce(function(sum, c) { return sum + (c.booking_count || 0); }, 0);
  const totalValue = customers.reduce(function(sum, c) { return sum + (c.total_value || 0); }, 0);

  function getStatusColor(status) {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'in_transit': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  return (
    <div className="space-y-6" data-testid="agent-customers">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-[32px] sv-display text-ink">Customers</h1>
          <p className="text-gray-600 mt-1">View and manage your assigned customers</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="rounded-full"
            onClick={loadData}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {pendingCount > 0 && (
            <Link to="/agent/approvals">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white rounded-sm">
                <Clock className="mr-2 h-4 w-4" />
                {pendingCount} Pending
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-sm shadow-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-100 rounded-sm flex items-center justify-center">
              <Users className="h-6 w-6 text-ink" />
            </div>
            <div>
              <p className="text-2xl font-normal tracking-[-0.01em] text-ink">{customers.length}</p>
              <p className="text-sm text-gray-500">Total customers</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-sm shadow-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-sm flex items-center justify-center">
              <UserCheck className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-normal tracking-[-0.01em] text-ink">{statusCounts.approved}</p>
              <p className="text-sm text-gray-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-sm shadow-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-sm flex items-center justify-center">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-normal tracking-[-0.01em] text-ink">{totalBookings}</p>
              <p className="text-sm text-gray-500">Total bookings</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-sm shadow-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-emerald-100 rounded-sm flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-normal tracking-[-0.01em] text-ink">{formatCurrency(totalValue)}</p>
              <p className="text-sm text-gray-500">Total revenue</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-[#0F1B26] to-[#00264d] rounded-sm p-5 flex flex-wrap gap-3">
        <Link to="/agent/approvals">
          <Button variant="secondary" className="rounded-sm">
            <UserCheck className="h-4 w-4 mr-2" />
            Review Approvals
          </Button>
        </Link>
        <Link to="/agent/bookings">
          <Button variant="secondary" className="rounded-sm">
            <Package className="h-4 w-4 mr-2" />
            All Bookings
          </Button>
        </Link>
        <Link to="/agent/walkin-booking">
          <Button variant="secondary" className="rounded-sm">
            <User className="h-4 w-4 mr-2" />
            Walk-in Booking
          </Button>
        </Link>
        <Link to="/agent/documents">
          <Button variant="secondary" className="rounded-sm">
            <FileText className="h-4 w-4 mr-2" />
            Documents
          </Button>
        </Link>
      </div>

      {/* Search & Filter */}
      <div className="bg-white rounded-sm shadow-card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search customers by name, email, or company..."
              className="pl-12 h-12 rounded-sm"
              value={searchTerm}
              onChange={function(e) { setSearchTerm(e.target.value); }}
              data-testid="customer-search"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'approved', 'pending_approval'].map(function(status) {
              const isActive = statusFilter === status;
              const label = status === 'all' ? 'All' : status === 'approved' ? 'Active' : 'Pending';
              const count = statusCounts[status] || 0;
              return (
                <button
                  key={status}
                  onClick={function() { setStatusFilter(status); }}
                  className={"px-4 py-2 rounded-full text-sm font-medium transition-colors " + (
                    isActive 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  )}
                >
                  {label} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Customers List */}
      <div className="bg-white rounded-sm shadow-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : filteredCustomers.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {filteredCustomers.map(function(customer) {
              return (
                <div 
                  key={customer.id} 
                  className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={function() { handleViewCustomer(customer); }}
                  data-testid={"customer-row-" + customer.id}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-[#1F97CC] to-[#0F1B26] rounded-sm flex items-center justify-center text-white font-medium text-lg">
                        {(customer.first_name || 'U')[0]}{(customer.last_name || 'N')[0]}
                      </div>
                      <div>
                        <h3 className="font-medium text-ink text-lg flex items-center gap-2">
                          {customer.first_name || 'Unknown'} {customer.last_name || 'User'}
                          <Badge className={customer.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                            {(customer.status || 'approved').replace('_', ' ')}
                          </Badge>
                        </h3>
                        <div className="flex items-center gap-4 text-gray-500 text-sm mt-1">
                          <span className="flex items-center gap-1">
                            <Mail className="h-4 w-4" />
                            {customer.email || 'No email'}
                          </span>
                          {customer.company && (
                            <span className="flex items-center gap-1">
                              <Building className="h-4 w-4" />
                              {customer.company}
                            </span>
                          )}
                          {customer.country && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {customer.country}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-2xl font-normal tracking-[-0.01em] text-ink">{customer.booking_count || 0}</p>
                        <p className="text-xs text-gray-500">Bookings</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-normal tracking-[-0.01em] text-blue-600">{formatCurrency(customer.total_value || 0)}</p>
                        <p className="text-xs text-gray-500">Total value</p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No customers found</p>
            <p className="text-sm text-gray-400 mt-1">Customers will appear here when they register from your region</p>
          </div>
        )}
      </div>

      {/* Customer Detail Modal */}
      <Dialog open={!!selectedCustomer} onOpenChange={closeModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedCustomer && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#1F97CC] to-[#0F1B26] rounded-sm flex items-center justify-center text-white font-medium text-xl">
                    {(selectedCustomer.first_name || 'U')[0]}{(selectedCustomer.last_name || 'N')[0]}
                  </div>
                  <div>
                    <h2 className="text-2xl font-normal tracking-[-0.01em] text-ink">
                      {selectedCustomer.first_name || 'Unknown'} {selectedCustomer.last_name || 'User'}
                    </h2>
                    <Badge className={selectedCustomer.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                      {(selectedCustomer.status || 'approved').replace('_', ' ')}
                    </Badge>
                  </div>
                </DialogTitle>
              </DialogHeader>

              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="bg-gray-50 rounded-sm p-4">
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <p className="font-medium text-ink flex items-center gap-2">
                    <Mail className="h-4 w-4 text-blue-600" />
                    {selectedCustomer.email || 'Not provided'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-sm p-4">
                  <p className="text-sm text-gray-500 mb-1">Phone</p>
                  <p className="font-medium text-ink flex items-center gap-2">
                    <Phone className="h-4 w-4 text-blue-600" />
                    {selectedCustomer.phone || 'Not provided'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-sm p-4">
                  <p className="text-sm text-gray-500 mb-1">Company</p>
                  <p className="font-medium text-ink flex items-center gap-2">
                    <Building className="h-4 w-4 text-blue-600" />
                    {selectedCustomer.company || 'Not provided'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-sm p-4">
                  <p className="text-sm text-gray-500 mb-1">Location</p>
                  <p className="font-medium text-ink flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-blue-600" />
                    {[selectedCustomer.city, selectedCustomer.country].filter(Boolean).join(', ') || 'Not provided'}
                  </p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="bg-ink rounded-sm p-4 text-white text-center">
                  <Package className="h-6 w-6 mx-auto mb-2 opacity-70" />
                  <p className="text-2xl font-normal tracking-[-0.01em]">{selectedCustomer.booking_count || 0}</p>
                  <p className="text-sm opacity-70">Total bookings</p>
                </div>
                <div className="bg-blue-500 rounded-sm p-4 text-white text-center">
                  <DollarSign className="h-6 w-6 mx-auto mb-2 opacity-70" />
                  <p className="text-2xl font-normal tracking-[-0.01em]">{formatCurrency(selectedCustomer.total_value || 0)}</p>
                  <p className="text-sm opacity-70">Total value</p>
                </div>
                <div className="bg-emerald-500 rounded-sm p-4 text-white text-center">
                  <TrendingUp className="h-6 w-6 mx-auto mb-2 opacity-70" />
                  <p className="text-2xl font-normal tracking-[-0.01em]">
                    {selectedCustomer.booking_count ? formatCurrency((selectedCustomer.total_value || 0) / selectedCustomer.booking_count) : '$0'}
                  </p>
                  <p className="text-sm opacity-70">Avg. Order Value</p>
                </div>
              </div>

              {/* Recent Bookings */}
              <div className="mt-6">
                <h3 className="font-medium text-ink mb-4 flex items-center gap-2">
                  <Package className="h-5 w-5 text-blue-600" />
                  Recent Bookings
                </h3>
                
                {loadingBookings ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  </div>
                ) : customerBookings.length > 0 ? (
                  <div className="space-y-3">
                    {customerBookings.slice(0, 5).map(function(booking) {
                      return (
                        <div key={booking.id} className="bg-gray-50 rounded-sm p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-ink">{booking.booking_number}</p>
                              <p className="text-sm text-gray-500">
                                {booking.origin_port} → {booking.destination_port}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {formatDate(booking.created_at)}
                              </p>
                            </div>
                            <div className="text-right">
                              <Badge className={getStatusColor(booking.status)}>
                                {booking.status.replace('_', ' ')}
                              </Badge>
                              <p className="font-medium text-ink mt-2">{formatCurrency(booking.price)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {customerBookings.length > 5 && (
                      <Link to={"/agent/bookings?customer=" + selectedCustomer.id} onClick={closeModal}>
                        <Button variant="outline" className="w-full rounded-sm">
                          View All {customerBookings.length} Bookings
                        </Button>
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-sm">
                    <Package className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No bookings yet</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6 pt-6 border-t">
                <Link to={"/agent/walkin-booking"} onClick={closeModal} className="flex-1">
                  <Button className="w-full bg-ink hover:bg-ink-800 rounded-sm">
                    <Package className="h-4 w-4 mr-2" />
                    Create New Booking
                  </Button>
                </Link>
                <Button variant="outline" className="rounded-sm" onClick={closeModal}>
                  Close
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AgentCustomers;
